import type { RefObject } from 'react';
import { Box, Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { scaledFontSize } from '../createWidgetTheme';

export const MAX_COMMENT_LENGTH = 200;

interface FeedbackCommentCardProps {
  commentId: string;
  comment: string;
  submitted: boolean;
  commentRef: RefObject<HTMLTextAreaElement | null>;
  onCommentChange: (value: string) => void;
  onSubmit: () => void;
}

export function FeedbackCommentCard({
  commentId,
  comment,
  submitted,
  commentRef,
  onCommentChange,
  onSubmit,
}: FeedbackCommentCardProps) {
  const theme = useTheme();
  const labelId = `${commentId}-label`;

  if (submitted) {
    return (
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          width: '100%',
          bgcolor: 'background.paper',
        }}
      >
        <Typography component="p" variant="subtitle2" fontWeight={500} color="text.secondary">
          Thank you for your feedback!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      id={commentId}
      sx={{
        mt: 1,
        p: 1.25,
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        id={labelId}
        variant="caption"
        fontWeight={600}
        sx={{ mb: 0.75, display: 'block' }}
      >
        What went wrong?
      </Typography>

      <TextField
        size="small"
        multiline
        minRows={2}
        maxRows={3}
        fullWidth
        placeholder="Tell us more so we can improve..."
        value={comment}
        inputRef={commentRef}
        onChange={e => {
          if (e.target.value.length <= MAX_COMMENT_LENGTH) onCommentChange(e.target.value);
        }}
        slotProps={{
          htmlInput: {
            maxLength: MAX_COMMENT_LENGTH,
            'aria-labelledby': labelId,
          },
        }}
        sx={{
          '& .MuiInputBase-root': { fontSize: scaledFontSize(theme, 12), p: 0.75 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
        }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.75 }}>
        <Typography variant="caption" fontSize={scaledFontSize(theme, 11)} color="text.disabled">
          {comment.length}/{MAX_COMMENT_LENGTH}
        </Typography>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={!comment.trim()}
          onClick={onSubmit}
          sx={{ textTransform: 'none', fontSize: scaledFontSize(theme, 12), px: 1.5, py: 0.25 }}
        >
          Submit
        </Button>
      </Stack>
    </Box>
  );
}
